import { useQueryClient } from '@tanstack/react-query';

import { useToast } from '@/components/feedback/toast';
import { useRealtimeChannel } from '@/hooks/use-realtime-channel';

import { useCommandStore } from '../command-store';
import { deviceKeys } from '../hooks';
import type { CommandType, DeviceCommand } from '../types';

const VERB: Record<CommandType, string> = {
  turn_on: 'Turn On',
  turn_off: 'Turn Off',
  stop_timer: 'Stop Timer',
  schedule: 'Schedule',
};

/**
 * App-level bridge for the command lifecycle (story 101849). A single `command-ack`
 * subscription mirrors every in-flight command into the shared command store and, on a
 * terminal transition, refreshes the device caches and toasts once — so the card pill,
 * power control, and detail banner all react no matter which view is mounted, and a
 * command queued from a card still resolves after navigating into its detail page.
 *
 * Renders nothing; mounted once inside `<AppShell>`.
 */
export function CommandAckBridge() {
  const qc = useQueryClient();
  const toast = useToast();

  useRealtimeChannel<DeviceCommand>('command-ack', (cmd) => {
    const { setCommand, clearCommand } = useCommandStore.getState();

    if (cmd.state === 'queued' || cmd.state === 'delivered') {
      setCommand(cmd);
      return;
    }

    // Terminal: executed | failed.
    clearCommand(cmd.deviceId);
    void qc.invalidateQueries({ queryKey: deviceKeys.list() });
    void qc.invalidateQueries({ queryKey: deviceKeys.detail(cmd.deviceId) });

    const verb = VERB[cmd.type];
    if (cmd.state === 'executed') toast.success({ title: `${verb} complete` });
    else toast.error({ title: `${verb} failed`, description: 'The device did not acknowledge.' });
  });

  return null;
}
